import { useCallback } from "react";
import { redactTelemetryValue } from "@/lib/telemetry-redaction";
import { supabase } from "@/integrations/supabase/client";
import { isSupabaseUnavailableError } from "@/lib/supabase-compat";

interface ErrorTelemetryPayload {
  errorMessage: string;
  errorName?: string | null;
  route: string;
  errorStack?: string | null;
  componentStack?: string | null;
  roleSummary?: string | null;
  metadata?: Record<string, unknown>;
}

const DEDUPE_WINDOW_MS = 60_000;
const recentErrorKeys = new Map<string, number>();
const PLACEHOLDER_SUPABASE_URL_RE = /your-(?:prod-)?project\.supabase\.co/i;

const isSupabaseUrlValid = () => {
  const value =
    (import.meta.env.VITE_SUPABASE_URL || (typeof process !== 'undefined' ? process.env.VITE_SUPABASE_URL : undefined) || "").trim().replace(/\/+$/, "");
  return Boolean(value && !PLACEHOLDER_SUPABASE_URL_RE.test(value));
};

const buildDedupeKey = (payload: ErrorTelemetryPayload) =>
  [
    payload.errorMessage,
    payload.errorStack ?? "no-stack",
    payload.componentStack ?? "no-component-stack",
    payload.route,
  ].join("|");

const shouldSkipDuplicate = (payload: ErrorTelemetryPayload) => {
  const now = Date.now();
  for (const [key, timestamp] of recentErrorKeys) {
    if (now - timestamp > DEDUPE_WINDOW_MS) {
      recentErrorKeys.delete(key);
    }
  }

  const key = buildDedupeKey(payload);
  const lastSeen = recentErrorKeys.get(key);
  if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) {
    return true;
  }
  recentErrorKeys.set(key, now);
  return false;
};

const normalizeMetadata = (
  payload: ErrorTelemetryPayload,
  context: { requestId: string; sessionId: string | null }
) => {
  const metadata = {
    ...(payload.metadata ?? {}),
    timestamp: new Date().toISOString(),
    errorName: payload.errorName ?? null,
    request_id: context.requestId,
    session_id: context.sessionId,
  };

  return Object.keys(metadata).length ? metadata : null;
};

const getClientContext = () => ({
  url: typeof window !== "undefined" ? window.location.href : null,
  userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
});

const decodeJwtPayload = (token: string) => {
  const payload = token.split(".")[1];
  if (!payload) {
    return null;
  }

  const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");

  try {
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const getSessionIdFromAuth = async () => {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  if (!accessToken) {
    return null;
  }

  const payload = decodeJwtPayload(accessToken);
  const sessionId = payload?.session_id;
  return typeof sessionId === "string" ? sessionId : null;
};

/**
 * Hook for logging errors to the error_telemetry table.
 * Does NOT require organization context - works anywhere in the app.
 * Uses the log_client_error RPC function for fire-and-forget logging.
 */
export function useErrorTelemetry() {
  const trackError = useCallback(async (payload: ErrorTelemetryPayload) => {
    await logErrorTelemetry(payload);
  }, []);

  return { trackError };
}

/**
 * Standalone function for logging errors without React context.
 * Useful for logging errors in non-component code (utils, services, etc.)
 */
export async function logErrorTelemetry(payload: ErrorTelemetryPayload): Promise<void> {
  if (!isSupabaseUrlValid()) {
    return;
  }

  // Sanitize before dedupe too: neither the in-memory keys nor either transport
  // should retain credentials accidentally supplied by a caller.
  payload = redactTelemetryValue(payload);
  if (shouldSkipDuplicate(payload)) {
    return;
  }

  const clientContext = redactTelemetryValue(getClientContext());
  const requestId = crypto.randomUUID();
  const sessionId = await getSessionIdFromAuth();
  const metadata = normalizeMetadata(payload, { requestId, sessionId });
  try {
    const { data, error } = await supabase.rpc("log_client_error", {
      _error_message: payload.errorMessage,
      _route: payload.route,
      _error_stack: payload.errorStack ?? null,
      _component_stack: payload.componentStack ?? null,
      _role_summary: payload.roleSummary ?? null,
      _url: clientContext.url,
      _user_agent: clientContext.userAgent,
      _metadata: metadata,
      _request_id: requestId,
      _session_id: sessionId,
    });
    if (error) {
      if (isSupabaseUnavailableError(error)) {
        return;
      }
      console.debug("[logErrorTelemetry] RPC returned an error:", redactTelemetryValue(error));
    } else if ((data as Record<string, unknown>)?.error) {
      if (isSupabaseUnavailableError((data as Record<string, unknown>).error)) {
        return;
      }
      console.debug("[logErrorTelemetry] RPC returned an error payload:", redactTelemetryValue((data as Record<string, unknown>).error));
    } else {
      return;
    }
  } catch (err) {
    if (!isSupabaseUnavailableError(err)) {
      console.debug("[logErrorTelemetry] Failed to log error:", redactTelemetryValue(err));
    }
    return;
  }

  try {
    const { data: authData } = await supabase.auth.getUser();
    const userId = authData?.user?.id ?? null;
    const { error } = await supabase.from("error_telemetry").insert({
      organization_id: null,
      user_id: userId,
      session_id: sessionId,
      request_id: requestId,
      error_message: payload.errorMessage,
      error_stack: payload.errorStack ?? null,
      component_stack: payload.componentStack ?? null,
      route: payload.route,
      url: clientContext.url,
      user_agent: clientContext.userAgent,
      role_summary: payload.roleSummary ?? null,
      metadata,
    });

    if (error) {
      if (isSupabaseUnavailableError(error)) {
        return;
      }
      console.debug("[logErrorTelemetry] Failed to insert error telemetry:", redactTelemetryValue(error));
    }
  } catch (err) {
    if (!isSupabaseUnavailableError(err)) {
      console.debug("[logErrorTelemetry] Fallback insert failed:", redactTelemetryValue(err));
    }
  }
}
